import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { ClickableRow } from '@/components/list/ClickableRow';
import { cn } from '@/lib/utils';
import { formatShortDate } from '../list/expense-labels';
import { formatAmount } from '../money';
import type { QueueItem } from '../reports/api-decisions';

const HEAD = 'px-3 py-2.5 font-semibold';

/** Past this many days a note's wait is drawn as needing attention (#952 L9). */
export const LONG_WAIT_DAYS = 7;

/**
 * «Attesa»: how long the note has waited; past `LONG_WAIT_DAYS` in the attention style, with an icon
 * and words, so it stands out without colour (U4).
 */
function Waiting({ item }: { readonly item: QueueItem }) {
  const { t } = useTranslation('expenses');
  const text =
    item.waitingDays <= 0
      ? t('approvals.waitingToday')
      : t('approvals.waiting', { count: item.waitingDays });
  if (item.waitingDays <= LONG_WAIT_DAYS) return <>{text}</>;
  return (
    <span
      data-tone="warning"
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold whitespace-nowrap',
        'bg-warning-tint text-warning',
      )}
    >
      <Clock aria-hidden="true" className="size-3.5" />
      {text}
      <span className="sr-only"> · {t('approvals.longWait')}</span>
    </span>
  );
}

function reportPath(item: QueueItem): string {
  return `/expenses/reports/${encodeURIComponent(item.id)}`;
}

/** Desktop: number, user, reason, total, sent, waiting; the row opens the report (spec §1.4, U5). */
export function QueueTable({ items }: { readonly items: readonly QueueItem[] }) {
  const { t } = useTranslation('expenses');
  const navigate = useNavigate();
  return (
    <div className="border-border hidden overflow-x-auto rounded-xl border @min-[620px]/shell:block">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{t('approvals.title')}</caption>
        <thead className="bg-muted/50 text-muted-foreground text-left text-xs tracking-wide uppercase">
          <tr>
            <th scope="col" className={HEAD}>
              {t('approvals.columns.number')}
            </th>
            <th scope="col" className={HEAD}>
              {t('approvals.columns.owner')}
            </th>
            <th scope="col" className={HEAD}>
              {t('approvals.columns.title')}
            </th>
            <th scope="col" className={`${HEAD} text-right`}>
              {t('approvals.columns.total')}
            </th>
            <th scope="col" className={HEAD}>
              {t('approvals.columns.sent')}
            </th>
            <th scope="col" className={HEAD}>
              {t('approvals.columns.waiting')}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <ClickableRow
              key={item.id}
              className="border-border border-t"
              onOpen={() => void navigate(reportPath(item))}
            >
              <td className="px-3 py-3 tabular-nums">
                <Link
                  to={reportPath(item)}
                  className="text-foreground font-semibold hover:underline"
                >
                  {item.number}
                </Link>
              </td>
              <td className="px-3 py-3">{item.owner.displayName}</td>
              <td className="px-3 py-3">{item.title}</td>
              <td className="px-3 py-3 text-right tabular-nums">{formatAmount(item.total)}</td>
              <td className="px-3 py-3 tabular-nums">
                {item.submittedAt ? formatShortDate(item.submittedAt) : ''}
              </td>
              <td className="px-3 py-3">
                <Waiting item={item} />
              </td>
            </ClickableRow>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Phone: a card per report, as in the list; opening it is where the decision is made. */
export function QueueCards({ items }: { readonly items: readonly QueueItem[] }) {
  const { t } = useTranslation('expenses');
  return (
    <ul aria-label={t('approvals.title')} className="flex flex-col gap-2 @min-[620px]/shell:hidden">
      {items.map((item) => (
        <li key={item.id}>
          <Link
            to={reportPath(item)}
            className="border-border bg-card flex items-start justify-between gap-3 rounded-xl border p-3"
          >
            <span className="flex min-w-0 flex-col gap-1">
              <b className="tabular-nums">{item.number}</b>
              <small className="text-muted-foreground">
                {item.owner.displayName} · {item.title}
              </small>
              <small className="text-muted-foreground">
                {item.submittedAt
                  ? `${t('approvals.sent', { date: formatShortDate(item.submittedAt) })} · `
                  : ''}
                <Waiting item={item} />
              </small>
            </span>
            <b className="shrink-0 tabular-nums">{formatAmount(item.total)}</b>
          </Link>
        </li>
      ))}
    </ul>
  );
}
